/**
 * /pipeline-facility/invoices — the PFI- invoice register
 * (GET /pipeline-facility/invoices, fieldiq-api#1229, PF-5a).
 *
 * An invoice is generated from customer-approved field tickets on "Ready to
 * invoice" (the band's action), so this register has no New button. The
 * status filter lives in the URL, so a record's back arrow returns to the list
 * as it was left. Chip counts are tallies of the loaded, unpaginated list.
 * Every total is the API's.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ClipboardCheck, FileClock, Send } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfMeta } from '../../../hooks/usePipelineFacility';
import { usePfBillable, usePfInvoices } from '../../../hooks/usePfInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { pfDay } from '../../../lib/pipeline-facility';
import { PF_INVOICE_STATUS_SKIN, pfInvoiceStatusSkin } from '../../../lib/pf-invoices';
import type { PfInvoiceRow } from '../../../types/pf-invoices';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_EYEBROW, PfBadge, PfChip, PfChipRow, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';

export const READY_PATH = '/pipeline-facility/invoices/ready';

/** A job column for a row: its job, or "Several jobs" when the invoice bills tickets from more than one. */
export const pfInvoiceJobText = (r: Pick<PfInvoiceRow, 'jobId' | 'jobNumber'>) => r.jobNumber || (r.jobId ? '—' : 'Several jobs');

export const pfInvoiceColumns: PfColumn<PfInvoiceRow>[] = [
  { key: 'number', header: 'Invoice', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
  { key: 'customer', header: 'Customer', render: (r) => <span className="font-semibold text-white">{r.customerName || '—'}</span> },
  { key: 'job', header: 'Job', render: (r) => <span className="font-mono">{pfInvoiceJobText(r)}</span> },
  { key: 'status', header: 'Status', render: (r) => <PfBadge skin={pfInvoiceStatusSkin(r.status)} value={r.status} /> },
  { key: 'date', header: 'Invoice date', render: (r) => pfDay(r.invoiceDate) },
  { key: 'due', header: 'Due', render: (r) => pfDay(r.dueDate) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="font-mono" data-testid="pf-invoice-row-total">{fmtCurrency(r.total, r.currency)}</span> },
];

export default function PfInvoicesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const setStatus = (next: string) => {
    const p = new URLSearchParams(params);
    if (next) p.set('status', next);
    else p.delete('status');
    setParams(p, { replace: true });
  };
  const catalog = usePfMeta().data?.data ?? null;
  const listQuery = usePfInvoices(status ? { status } : undefined);
  const allQuery = usePfInvoices();
  const billable = usePfBillable();
  const rows = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: PfInvoiceRow) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const billableKnown = !billable.isLoading && !queryLoadFailure(billable);
  const readyCount = billableKnown ? (billable.data?.data ?? []).reduce((n, g) => n + g.tickets.length, 0) : undefined;
  const statuses = catalog?.invoiceStatuses?.length
    ? catalog.invoiceStatuses
    : Object.entries(PF_INVOICE_STATUS_SKIN).map(([value, s]) => ({ value, label: s.label }));

  return (
    <div data-testid="pf-invoices-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Invoices"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <Link to={READY_PATH} className="fi-action fi-action--standard fi-action--primary" data-testid="pf-invoices-ready-link">
            Ready to invoice
          </Link>
        }
        stats={[
          { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: 'Ready', sub: 'Approved tickets', tone: 'accent', loading: billable.isLoading, value: displayValue(readyCount) },
          { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'Not yet sent', tone: 'neutral', loading: allQuery.isLoading, value: countOf((r) => r.status === 'draft') },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'Sent', sub: 'With the customer', tone: 'info', loading: allQuery.isLoading, value: countOf((r) => r.status === 'sent' || r.status === 'approved') },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Paid', tone: 'success', loading: allQuery.isLoading, value: countOf((r) => r.status === 'paid') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            An invoice bills field tickets the customer has approved — one customer, one currency, one province or state. Make one from{' '}
            <Link className="underline" to={READY_PATH}>Ready to invoice</Link>.
          </p>
          <PfChipRow label="Filter by status">
            <PfChip label="All" active={!status} onClick={() => setStatus('')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <PfChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.status === s.value)} />
            ))}
          </PfChipRow>
          <PfPanel
            eyebrow="Billed from field tickets"
            title="Invoices"
            testId="pf-invoice-register"
            footer={!listQuery.isLoading && !queryLoadFailure(listQuery) && rows.length ? `${rows.length} invoice${rows.length === 1 ? '' : 's'}` : undefined}
          >
            <PfRegister
              query={listQuery}
              rows={rows}
              columns={pfInvoiceColumns}
              rail={(r) => pfInvoiceStatusSkin(r.status).rail}
              onOpen={(r) => openRecord(`/pipeline-facility/invoices/${r.id}`)}
              subject="invoices"
              testId="pf-invoice"
              minWidth="min-w-[960px]"
              empty={status ? <PfEmpty>No invoice has this status.</PfEmpty> : <PfEmpty>No invoices yet. Make one from Ready to invoice.</PfEmpty>}
              card={(r) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
                      <p className="mt-0.5 truncate text-xs text-fieldiq-muted">
                        {pfInvoiceJobText(r)} · {r.customerName || '—'}
                      </p>
                    </div>
                    <PfBadge skin={pfInvoiceStatusSkin(r.status)} value={r.status} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                    <span>Dated {pfDay(r.invoiceDate)}</span>
                    <span>Due {pfDay(r.dueDate)}</span>
                    <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span>
                  </div>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
